/**
 * useLienWorkflowConfig
 *
 * Fetches the firm's lien management workflow configuration.
 * Used to display firm preferences in lien notice package workspace.
 */

import { useMemo } from 'react';

import type { LienManagementWorkflowConfig } from '@/services/api/generated/firmsService';
import { enhancedApi as firmsApi } from '@/services/api/generated/firmsService';
import { mattersApiClient } from '@/services/api/mattersService';

const LIENS_SERVICE_NAME = 'liens';
const LIEN_MANAGEMENT_WORKFLOW_CODE = 'lien_management';

// The generated FirmServiceWorkflowSchema has workflow_code typed as WorkflowCode enum,
// but lien_management may not be regenerated yet. Use a flexible type for matching.
interface WorkflowWithConfig {
  workflow_code: string;
  is_enabled: boolean;
  notes?: string;
  config?: LienManagementWorkflowConfig | Record<string, unknown> | null;
}

export interface UseLienWorkflowConfigOptions {
  matterId: string;
  skip?: boolean;
}

export interface UseLienWorkflowConfigResult {
  config: LienManagementWorkflowConfig | null;
  isEnabled: boolean;
  notes: string | null;
  isLoading: boolean;
}

export const useLienWorkflowConfig = ({
  matterId,
  skip = false,
}: UseLienWorkflowConfigOptions): UseLienWorkflowConfigResult => {
  // Fetch matter to get firm ID
  const { data: matter, isLoading: isMatterLoading } =
    mattersApiClient.useGetMatterQuery(matterId, { skip: skip || !matterId });

  const firmId = matter?.firm?.id;

  // Fetch firm services to get lien management workflow config
  const { data: firmServices, isLoading: isFirmServicesLoading } =
    firmsApi.useGetFirmServicesQuery(
      { firmId: firmId || '' },
      { skip: skip || !firmId },
    );

  const result = useMemo((): Omit<UseLienWorkflowConfigResult, 'isLoading'> => {
    if (!firmServices) {
      return { config: null, isEnabled: false, notes: null };
    }

    // Find the liens service
    const liensService = firmServices.find(
      (service) => service.service?.name === LIENS_SERVICE_NAME,
    );

    if (!liensService?.is_active) {
      return { config: null, isEnabled: false, notes: null };
    }

    // Find the lien_management workflow
    // Cast to flexible type since workflow_code may include values not yet in generated types
    const workflows = (liensService.workflows ?? []) as WorkflowWithConfig[];
    const lienWorkflow = workflows.find(
      (w) => w.workflow_code === LIEN_MANAGEMENT_WORKFLOW_CODE,
    );

    if (!lienWorkflow?.is_enabled) {
      return { config: null, isEnabled: false, notes: null };
    }

    // Extract config (safely cast since we know this is LienManagementWorkflowConfig)
    const config = lienWorkflow.config as
      | LienManagementWorkflowConfig
      | undefined;

    return {
      config: config ?? null,
      isEnabled: true,
      notes: lienWorkflow.notes || null,
    };
  }, [firmServices]);

  return {
    ...result,
    isLoading: isMatterLoading || isFirmServicesLoading,
  };
};
